/**
 * Application Review Modal Component
 * Modal for reviewing and approving/rejecting CBO applications
 */

import React, { useState } from 'react';
import { X, CheckCircle, XCircle, TrendingUp, Users, Award, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { sponsorApplicationsAPI, type CBOApplication } from '@/services/api/sponsorApplications';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
interface ApplicationReviewModalProps {
  application: CBOApplication;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
}

export const ApplicationReviewModal: React.FC<ApplicationReviewModalProps> = ({
  application,
  onClose,
  onApprove,
  onReject
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [action, setAction] = useState<'review' | 'approve' | 'reject'>('review');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [enrollmentStartDate, setEnrollmentStartDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async () => {
    if (!approvalNotes.trim()) {
      setError('Please provide approval notes');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await sponsorApplicationsAPI.approveApplication(application.id, {
        approval_notes: approvalNotes,
        enrollment_start_date: enrollmentStartDate
      });
      onApprove();
    } catch (err: any) {
      console.error('Failed to approve application:', err);
      setError(err.message || 'Failed to approve application');
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await sponsorApplicationsAPI.rejectApplication(application.id, {
        rejection_reason: rejectionReason
      });
      onReject();
    } catch (err: any) {
      console.error('Failed to reject application:', err);
      setError(err.message || 'Failed to reject application');
      setLoading(false);
    }
  };

  const getMatchQualityColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 80) return 'text-blue-600 bg-blue-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('sponsors.review_cbo_application', 'Review CBO Application')}</DialogTitle>
          <DialogDescription>
            {t('sponsors.evaluate_the_application_and_decide', 'Evaluate the application and decide whether to approve or reject')}
          </DialogDescription>
        </DialogHeader>

        {action === 'review' && (
          <div className="space-y-6">
            {/* CBO Header */}
            <div className="border-b pb-4">
              <h3 className="text-xl font-bold mb-2">{application.cbo_name}</h3>
              <p className="text-sm text-gray-600">
                {t('sponsors.applying_for', 'Applying for:')} <span className="font-medium">{application.program_name}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Application Date: {fmt.short(application.application_date)}
              </p>
            </div>

            {/* Match Score */}
            <div className={`rounded-lg p-4 ${getMatchQualityColor(application.match_score_at_application)}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t('sponsors.match_score', 'Match Score')}</p>
                  <p className="text-3xl font-bold">
                    {application.match_score_at_application.toFixed(1)}%
                  </p>
                </div>
                <Award className="w-12 h-12 opacity-50" />
              </div>
            </div>

            {/* CBO Metrics Grid */}
            <div className="grid grid-cols-2 gap-4">
              {application.cbo_quality_score && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    <p className="text-sm font-medium text-gray-700">{t('sponsors.quality_score', 'Quality Score')}</p>
                  </div>
                  <p className="text-2xl font-bold">{application.cbo_quality_score.toFixed(1)}/10</p>
                </div>
              )}

              {application.cbo_current_members !== undefined && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-green-600" />
                    <p className="text-sm font-medium text-gray-700">{t('sponsors.member_capacity', 'Member Capacity')}</p>
                  </div>
                  <p className="text-2xl font-bold">
                    {application.cbo_current_members}/{application.cbo_capacity}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {application.cbo_capacity && application.cbo_current_members
                      ? `${((application.cbo_capacity - application.cbo_current_members) / application.cbo_capacity * 100).toFixed(0)}% available`
                      : ''}
                  </p>
                </div>
              )}

              {application.cbo_success_rate && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-sm font-medium text-gray-700">{t('sponsors.success_rate', 'Success Rate')}</p>
                  </div>
                  <p className="text-2xl font-bold">{application.cbo_success_rate.toFixed(1)}%</p>
                </div>
              )}

              {application.predicted_enrollments && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    <p className="text-sm font-medium text-gray-700">{t('sponsors.predicted_enrollments', 'Predicted Enrollments')}</p>
                  </div>
                  <p className="text-2xl font-bold">{application.predicted_enrollments}</p>
                  <p className="text-xs text-gray-500 mt-1">per year</p>
                </div>
              )}
            </div>

            {/* Application Message */}
            {application.application_message && (
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('sponsors.application_message', 'Application Message')}</Label>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {application.application_message}
                  </p>
                </div>
              </div>
            )}

            {/* Predictions */}
            {application.predicted_roi && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-900 mb-2">{t('sponsors.performance_prediction', 'Performance Prediction')}</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-blue-700">{t('sponsors.estimated_roi', 'Estimated ROI')}</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {application.predicted_roi.toFixed(1)}x
                    </p>
                  </div>
                  {application.predicted_enrollments && (
                    <div className="text-right">
                      <p className="text-xs text-blue-700">{t('sponsors.annual_enrollments', 'Annual Enrollments')}</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {application.predicted_enrollments}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={loading}>
                {t('sponsors.cancel', 'Cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setAction('reject')}
                disabled={loading}
              >
                <XCircle className="w-4 h-4 mr-2" />
                {t('sponsors.reject', 'Reject')}
              </Button>
              <Button
                onClick={() => setAction('approve')}
                disabled={loading}
                className="bg-v2accent hover:bg-v2accent/90"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('sponsors.approve', 'Approve')}
              </Button>
            </DialogFooter>
          </div>
        )}

        {action === 'approve' && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="font-medium text-green-900">Approving: {application.cbo_name}</p>
              <p className="text-sm text-green-700 mt-1">
                {t('sponsors.this_cbo_will_be_added', 'This CBO will be added to your network and can begin enrollments')}
              </p>
            </div>

            <div>
              <Label htmlFor="enrollment-start-date">{t('sponsors.enrollment_start_date', 'Enrollment Start Date')}</Label>
              <Input
                id="enrollment-start-date"
                type="date"
                value={enrollmentStartDate}
                onChange={(e) => setEnrollmentStartDate(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="approval-notes">{t('sponsors.approval_notes', 'Approval Notes *')}</Label>
              <Textarea
                id="approval-notes"
                placeholder="Add notes about this approval (e.g., expectations, onboarding steps, contact person...)"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                rows={4}
                className="mt-1"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setAction('review')} disabled={loading}>
                {t('sponsors.back', 'Back')}
              </Button>
              <Button onClick={handleApprove} disabled={loading} className="bg-v2accent hover:bg-v2accent/90">
                {loading ? 'Approving...' : 'Confirm Approval'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {action === 'reject' && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="font-medium text-red-900">Rejecting: {application.cbo_name}</p>
              <p className="text-sm text-red-700 mt-1">
                {t('sponsors.the_cbo_will_be_notified', 'The CBO will be notified of the rejection with your reason')}
              </p>
            </div>

            <div>
              <Label htmlFor="rejection-reason">{t('sponsors.rejection_reason', 'Rejection Reason *')}</Label>
              <Textarea
                id="rejection-reason"
                placeholder="Explain why this application is being rejected (e.g., insufficient capacity, doesn't meet requirements, geographic mismatch...)"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="mt-1"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setAction('review')} disabled={loading}>
                {t('sponsors.back_1', 'Back')}
              </Button>
              <Button variant="destructive" onClick={handleReject} disabled={loading}>
                {loading ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
